import type { ReactNode } from 'react'
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react'

export interface SortableThProps {
  /** Backend sort key this header controls (matches the controller allow-list). */
  sortKey: string
  /** Header label. */
  children: ReactNode
  /** Currently-active sort key (page state). */
  sortBy?: string
  /** Currently-active sort order. */
  sortOrder?: 'asc' | 'desc'
  /** Called with the header's sortKey when the user activates it. */
  onSort?: (key: string) => void
  /** Extra classes for the <th> (e.g. text-right, hidden md:table-cell). */
  className?: string
}

/**
 * EP-97: a sortable table header for pages built on a raw <table> (Invoices,
 * Payments, Leases, Debt Cases) rather than the shared DataTable. Mirrors
 * DataTable's sortable-header semantics: clickable + keyboard-activable
 * (role="button", Enter/Space), aria-sort, and the same sort-direction icons.
 */
export function SortableTh({
  sortKey,
  children,
  sortBy,
  sortOrder,
  onSort,
  className = '',
}: SortableThProps) {
  const active = sortBy === sortKey
  const ariaSort: React.AriaAttributes['aria-sort'] = active
    ? sortOrder === 'asc'
      ? 'ascending'
      : 'descending'
    : 'none'

  const icon = !active ? (
    <ChevronsUpDown className="w-3 h-3 opacity-40" />
  ) : sortOrder === 'asc' ? (
    <ChevronUp className="w-3 h-3" />
  ) : (
    <ChevronDown className="w-3 h-3" />
  )

  return (
    <th
      aria-sort={ariaSort}
      className={`cursor-pointer select-none hover:bg-base-200 ${className}`}
      onClick={onSort ? () => onSort(sortKey) : undefined}
    >
      <span
        role="button"
        tabIndex={0}
        className="inline-flex items-center gap-1"
        onKeyDown={(e) => {
          if (onSort && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault()
            onSort(sortKey)
          }
        }}
      >
        {children}
        {icon}
      </span>
    </th>
  )
}
